import { Checkbox } from '@/components/form/Checkbox';
import { FormGroup } from '@/components/form/FormGroup';
import {
  convertUTCTimeToLocal,
  formatDayOffset,
} from '@/sections/attacks/capabilities/utils/timezone';
import { cn } from '@/utils/classname';

export interface DaySchedule {
  enabled: boolean;
  time: string; // HH:MM format (24-hour)
}

export interface WeeklyScheduleState {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

interface WeeklyScheduleProps {
  schedule: WeeklyScheduleState;
  onScheduleChange: (schedule: WeeklyScheduleState) => void;
  error?: string;
}

const DAYS: Array<keyof WeeklyScheduleState> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DAY_LABELS: Record<keyof WeeklyScheduleState, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

/**
 * WeeklySchedule - Select days and times for weekly scheduled execution
 *
 * Allows operators to select specific days of the week and configure
 * different execution times for each selected day.
 *
 * @param schedule - Current weekly schedule state
 * @param onScheduleChange - Callback when schedule changes
 * @param error - Optional error message to display
 */
export function WeeklySchedule({
  schedule,
  onScheduleChange,
  error,
}: WeeklyScheduleProps) {
  const handleDayToggle = (day: keyof WeeklyScheduleState) => {
    onScheduleChange({
      ...schedule,
      [day]: {
        ...schedule[day],
        enabled: !schedule[day].enabled,
      },
    });
  };

  const handleTimeChange = (day: keyof WeeklyScheduleState, time: string) => {
    onScheduleChange({
      ...schedule,
      [day]: {
        ...schedule[day],
        time,
      },
    });
  };

  const hasEnabledDays = DAYS.some(day => schedule[day].enabled);

  return (
    <div className="space-y-4">
      <FormGroup
        label="Execution Schedule (UTC)"
        name="weeklySchedule"
        info={{
          text: 'Select days and times for capability execution. All times are in UTC timezone.',
        }}
      >
        <div className="space-y-2">
          {DAYS.map(day => (
            <div
              key={day}
              className={cn(
                'flex items-center gap-4 rounded-md p-3 transition-colors',
                schedule[day].enabled
                  ? 'bg-chariot-background-secondary'
                  : 'bg-transparent'
              )}
            >
              <div className="w-32 flex-shrink-0">
                <Checkbox
                  name={`schedule-${day}`}
                  value={schedule[day].enabled ? 'true' : 'false'}
                  onChange={() => handleDayToggle(day)}
                  label={DAY_LABELS[day]}
                  data-testid={`schedule-checkbox-${day}`}
                />
              </div>

              {schedule[day].enabled && (
                <div className="flex flex-1 items-center gap-2">
                  <input
                    type="time"
                    name={`time-${day}`}
                    id={`time-${day}`}
                    data-testid={`schedule-time-${day}`}
                    aria-label={`Execution time for ${DAY_LABELS[day]}`}
                    className={cn(
                      'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
                      'placeholder-chariot-text-tertiary w-full rounded-md border px-3 py-2 text-sm',
                      'focus:border-chariot-brand-primary transition-colors focus:outline-none',
                      'cursor-pointer'
                    )}
                    value={schedule[day].time}
                    onChange={e => handleTimeChange(day, e.target.value)}
                    required
                  />
                  <div className="text-chariot-text-tertiary flex items-center gap-1 text-sm whitespace-nowrap">
                    <span>UTC</span>
                    {schedule[day].time && (
                      <>
                        <span>·</span>
                        <span>
                          {(() => {
                            const local = convertUTCTimeToLocal(
                              schedule[day].time
                            );
                            const dayOffsetText = formatDayOffset(
                              local.dayOffset
                            );
                            return `${local.time} ${local.timezone}${dayOffsetText ? ` (${dayOffsetText})` : ''}`;
                          })()}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {!hasEnabledDays && (
          <p className="text-chariot-text-tertiary mt-2 text-sm">
            Select at least one day to enable scheduled execution
          </p>
        )}
      </FormGroup>

      {error && <div className="text-sm text-red-500">{error}</div>}
    </div>
  );
}
